<template>
  <div class="mt-4">
    <v-alert v-if="lastError" dense type="error" outlined dismissible class="mx-auto mb-2" max-width="95%"
      @input="lastError = ''">
      <strong>API 호출 실패:</strong>
      <div style="font-size:11px;white-space:pre-wrap;">{{ lastError }}</div>
    </v-alert>
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark dense color="indigo darken-2">
        <v-toolbar-title>
          <v-icon left small>mdi-store-search</v-icon>매체 상세 통계
        </v-toolbar-title>
      </v-toolbar>
      <v-card-text>
        <v-row dense>
          <v-col cols="6" md="3">
            <v-autocomplete dense outlined hide-details clearable
              label="매체 (필수)"
              :items="mediaItems" :loading="loadingMedia"
              item-text="label" item-value="app_key"
              v-model="appKey"
              no-data-text="매체 없음" placeholder="검색 가능"></v-autocomplete>
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field dense outlined hide-details type="date" label="일자 From" v-model="fromDate"></v-text-field>
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field dense outlined hide-details type="date" label="일자 To" v-model="toDate"></v-text-field>
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field dense outlined hide-details type="month" label="월 From" v-model="fromYm"></v-text-field>
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field dense outlined hide-details type="month" label="월 To" v-model="toYm"></v-text-field>
          </v-col>
          <v-col cols="6" md="3">
            <v-btn color="primary" :loading="loading" @click="load" :disabled="!appKey" block>조회</v-btn>
          </v-col>
        </v-row>
        <div v-if="!appKey" class="text-caption red--text mt-2">app_key 를 입력하세요.</div>
      </v-card-text>
    </v-card>

    <!-- 핵심 지표 카드 -->
    <v-card v-if="totals" class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-toolbar dark dense color="teal darken-2">
        <v-toolbar-title>합계 ({{ appKey }})</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pa-3">
        <v-row dense>
          <v-col cols="6" md="3"><v-card flat color="#E8F5E9" class="pa-3">
            <div class="text-caption grey--text">총 베팅금</div>
            <div class="text-h6 font-weight-bold">{{ fmt(totals.bet) }}</div>
          </v-card></v-col>
          <v-col cols="6" md="3"><v-card flat color="#E3F2FD" class="pa-3">
            <div class="text-caption grey--text">총 지급액</div>
            <div class="text-h6 font-weight-bold">{{ fmt(totals.payout) }}</div>
          </v-card></v-col>
          <v-col cols="6" md="3"><v-card flat color="#FFF3E0" class="pa-3">
            <div class="text-caption grey--text">순수익</div>
            <div class="text-h6 font-weight-bold" :class="totals.net >= 0 ? 'green--text' : 'red--text'">{{ fmt(totals.net) }}</div>
          </v-card></v-col>
          <v-col cols="6" md="3"><v-card flat color="#F3E5F5" class="pa-3">
            <div class="text-caption grey--text">총 베팅수</div>
            <div class="text-h6 font-weight-bold">{{ fmt(totals.bets) }}</div>
          </v-card></v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 일별 line -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-toolbar dark dense color="indigo">
        <v-toolbar-title>일별 추이 (베팅금/지급액/순수익)</v-toolbar-title>
      </v-toolbar>
      <v-card-text>
        <line-chart v-if="dailyChart.datasets.length" :chart-data="dailyChart" :options="chartOptions" :height="90"></line-chart>
        <div v-else class="text-center grey--text pa-6">데이터 없음</div>
      </v-card-text>
    </v-card>

    <!-- 일별 테이블 -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-toolbar dark dense color="indigo lighten-1">
        <v-toolbar-title>일별 raw</v-toolbar-title>
      </v-toolbar>
      <v-data-table dense :headers="dailyHeaders" :items="daily" :items-per-page="50" :loading="loading">
        <template v-slot:item.total_bet_amount="{ item }">{{ fmt(item.total_bet_amount) }}</template>
        <template v-slot:item.total_payout_amount="{ item }">{{ fmt(item.total_payout_amount) }}</template>
        <template v-slot:item.net_revenue="{ item }">
          <span :class="item.net_revenue >= 0 ? 'green--text' : 'red--text'">{{ fmt(item.net_revenue) }}</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- 월별 테이블 -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-toolbar dark dense color="deep-purple darken-2">
        <v-toolbar-title>월별 raw (MAU)</v-toolbar-title>
      </v-toolbar>
      <v-data-table dense :headers="monthlyHeaders" :items="monthly" :items-per-page="24" :loading="loading">
        <template v-slot:item.total_bet_amount="{ item }">{{ fmt(item.total_bet_amount) }}</template>
        <template v-slot:item.total_payout_amount="{ item }">{{ fmt(item.total_payout_amount) }}</template>
        <template v-slot:item.net_revenue="{ item }">
          <span :class="item.net_revenue >= 0 ? 'green--text' : 'red--text'">{{ fmt(item.net_revenue) }}</span>
        </template>
      </v-data-table>
    </v-card>

    <div class="py-12"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import LineChart from './charts/LineChart.js';

function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function curYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function shiftYm(ym, delta) {
  let [y, m] = ym.split('-').map(Number);
  m += delta;
  while (m < 1) { m += 12; y -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  return `${y}-${String(m).padStart(2, '0')}`;
}

export default {
  name: 'Danta_Stats_Media',
  components: { LineChart },
  data() {
    const today = new Date();
    const monthAgo = new Date(today.getTime() - 30 * 24 * 3600 * 1000);
    return {
      loading: false,
      lastError: '',
      appKey: '',
      mediaItems: [],
      loadingMedia: false,
      fromDate: fmtDate(monthAgo),
      toDate:   fmtDate(today),
      fromYm:   shiftYm(curYm(), -5),
      toYm:     curYm(),
      daily: [],
      monthly: [],
      dailyHeaders: [
        { text: '일자', value: 'stat_date' },
        { text: 'DAU', value: 'active_user_count', align: 'right' },
        { text: '신규', value: 'new_user_count', align: 'right' },
        { text: '베팅', value: 'bet_count', align: 'right' },
        { text: '취소', value: 'cancelled_count', align: 'right' },
        { text: '베팅금', value: 'total_bet_amount', align: 'right' },
        { text: '지급액', value: 'total_payout_amount', align: 'right' },
        { text: '순수익', value: 'net_revenue', align: 'right' },
      ],
      monthlyHeaders: [
        { text: '월', value: 'year_month' },
        { text: 'MAU', value: 'active_user_count', align: 'right' },
        { text: '신규', value: 'new_user_count', align: 'right' },
        { text: '베팅', value: 'bet_count', align: 'right' },
        { text: '취소', value: 'cancelled_count', align: 'right' },
        { text: '베팅금', value: 'total_bet_amount', align: 'right' },
        { text: '지급액', value: 'total_payout_amount', align: 'right' },
        { text: '순수익', value: 'net_revenue', align: 'right' },
      ],
    };
  },
  computed: {
    totals() {
      if (!this.daily.length) return null;
      let bet = 0, payout = 0, bets = 0;
      for (const r of this.daily) {
        bet    += Number(r.total_bet_amount    || 0);
        payout += Number(r.total_payout_amount || 0);
        bets   += Number(r.bet_count           || 0);
      }
      return { bet, payout, net: bet - payout, bets };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: { yAxes: [{ ticks: { beginAtZero: true } }] },
      };
    },
    dailyChart() {
      const sorted = this.daily.slice().sort((a, b) => String(a.stat_date).localeCompare(String(b.stat_date)));
      if (!sorted.length) return { labels: [], datasets: [] };
      const labels = sorted.map(r => String(r.stat_date).substring(0, 10));
      const bet    = sorted.map(r => Number(r.total_bet_amount    || 0));
      const payout = sorted.map(r => Number(r.total_payout_amount || 0));
      const net    = sorted.map(r => Number(r.net_revenue         || 0));
      return {
        labels,
        datasets: [
          { label: '베팅금',  borderColor: '#3f51b5', backgroundColor: 'rgba(63,81,181,0.10)', data: bet,    fill: false },
          { label: '지급액',  borderColor: '#e91e63', backgroundColor: 'rgba(233,30,99,0.10)', data: payout, fill: false },
          { label: '순수익',  borderColor: '#43a047', backgroundColor: 'rgba(67,160,71,0.10)', data: net,    fill: false },
        ],
      };
    },
  },
  created() { this.loadMediaList(); },
  methods: {
    ...mapActions(['DANTA_STATS_DAILY_MEDIA', 'DANTA_STATS_MONTHLY_MEDIA', 'DANTA_GET_MEDIA_LIST']),
    fmt(n) { return n == null ? '-' : Number(n).toLocaleString(); },
    async loadMediaList() {
      this.loadingMedia = true;
      try {
        const r = await this.DANTA_GET_MEDIA_LIST({ client_id: null });
        const rows = (r && r.media_list) || [];
        this.mediaItems = rows.map(m => ({
          app_key: m.app_key,
          label: `${m.media_name || m.media_id || m.app_key}  [${m.app_key}]`,
        }));
      } catch (err) { console.warn('media list load 실패:', err); }
      finally { this.loadingMedia = false; }
    },
    async load() {
      if (!this.appKey) return;
      this.loading = true;
      try {
        const [d, m] = await Promise.all([
          this.DANTA_STATS_DAILY_MEDIA({ from: this.fromDate, to: this.toDate, app_key: this.appKey }),
          this.DANTA_STATS_MONTHLY_MEDIA({ from: this.fromYm,  to: this.toYm,  app_key: this.appKey }),
        ]);
        this.daily   = (d && d.rows) || [];
        this.monthly = (m && m.rows) || [];
        this.lastError = '';
      } catch (err) {
        this.lastError = (err && (err.message || err.err_message)) || String(err);
        console.error('[Stats_Media] load 실패:', err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
